import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getProductBySlug } from "../../api/productService";
import { getErrorMessage } from "../../utils/errorHandler";

export default function AdminProductView() {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [featuredImageUrl, setFeaturedImageUrl] = useState("");
  const { slug } = useParams();
  const [variants, setVariants] = useState([]);

  useEffect(() => {
    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  const fetchProduct = async () => {
    try {
      setLoading(true);

      const response = await getProductBySlug(slug);
      setFeaturedImageUrl(response?.data?.featuredImageUrl);
      setVariants(response?.data?.variants);
      setProduct(response?.data?.product || response?.data);
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid p-5 text-center">
        <h5>Loading Product...</h5>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-fluid p-5 text-center">
        <h5>Product Not Found</h5>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="row">
        {/* LEFT SIDE */}
        <div className="col-lg-5">
          {/* FEATURED IMAGE */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <img
                src={product?.seo?.metaImageUrl || "/default-product.png"}
                alt={product?.productName}
                className="w-100 rounded"
                style={{
                  height: "400px",
                  objectFit: "cover",
                }}
              />
            </div>
          </div>

          {/* GALLERY */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-3">Gallery Images</h5>

              <div className="row">
                {product?.imageUrls?.length > 0 ? (
                  product?.imageUrls?.map((img, index) => (
                    <div className="col-4 mb-3" key={index}>
                      <img
                        src={img}
                        alt="gallery"
                        className="w-100 rounded border"
                        style={{
                          height: "100px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  ))
                ) : (
                  <p>No Images</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              {/* PRODUCT NAME */}
              <h3 className="mb-2">{product?.productName || "-"}</h3>

              {/* BADGES */}
              <div className="mb-3 d-flex flex-wrap gap-2">
                {product?.badges?.map((badge, index) => (
                  <span
                    key={index}
                    style={{
                      padding: "6px 16px",
                      fontSize: "12px",
                      borderRadius: "50px",
                      border: "1px solid #28a745",
                      fontWeight: "500",
                    }}
                  >
                    {badge}
                  </span>
                ))}
              </div>

              {/* PRICE */}
              <div className="mb-4">
                <h3 className="text-success mb-1">
                  ₹{variants[0]?.price || 0}
                </h3>
              </div>

              {/* DETAILS */}
              <div className="table-responsive">
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <th>Preview</th>
                      <td
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          fontFamily: "inherit",
                          lineHeight: "1.6",
                        }}
                      >
                        {product?.preview?.trim() || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Slug</th>
                      <td>{product?.seo?.slug}</td>
                    </tr>

                    <tr>
                      <th>Category</th>
                      <td>{product?.category}</td>
                    </tr>

                    <tr>
                      <th>SKU</th>
                      <td>{product?.sku}</td>
                    </tr>

                    <tr>
                      <th>Stock</th>
                      <td>{product?.maxQuantity}</td>
                    </tr>

                    <tr>
                      <th>Featured</th>
                      <td>{product?.isFeatured ? "Yes" : "No"}</td>
                    </tr>

                    <tr>
                      <th>Status</th>
                      <td>{product?.status}</td>
                    </tr>

                    <tr>
                      <th>In Stock</th>
                      <td>{product?.inStock ? "Available" : "Out Of Stock"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* DESCRIPTION */}
              <div className="mt-4">
                <h5>Description</h5>

                <p className="text-muted">{product?.productDescription}</p>
              </div>

              {/* VARIANTS */}
              <div className="mt-5">
                <h4 className="mb-4">Variants</h4>

                {variants.length > 0 ? (
                  <div className="row">
                    {variants.map((variant, index) => (
                      <div
                        className="col-md-6 mb-4"
                        key={variant?._id || index}
                      >
                        <div className="card border shadow-sm">
                          <div className="p-2">
                            <h5 className="mb-1">Variant #{index + 1}</h5>

                            <p className="mb-0">
                              <strong className="text-sm">Font:</strong>
                              {variant?.fontId?.value || "-"}
                            </p>

                            <p className="mb-0">
                              <strong className="text-sm">Size:</strong>
                              {variant?.sizeId?.value || "-"}
                            </p>

                            <p className="mb-0">
                              <strong className="text-sm">Material:</strong>

                              {variant?.materialId?.value || "-"}
                            </p>

                            <p className="mb-0">
                              <strong className="text-sm">Color:</strong>
                              {variant?.colorId?.name || "-"}
                            </p>

                            <p className="mb-0">
                              <strong className="text-sm">Price:</strong>₹
                              {variant?.price || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No Variants Available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
